// Convex Auth-backed replacement for the old Supabase-Auth useAuth.tsx. See
// CONVEX_MIGRATION_STATUS.md and ~/convex-migration/trialsim/NOTES.md §1 for
// the design rationale (one auth system, no resolveUserId bridge, explicit
// completeSignup mutation instead of an auto-provision side effect).
//
// The external AuthContextType shape is kept identical to the old hook on
// purpose — every consumer (ProtectedRoute, LoginPage, RegisterPage,
// ForgotPasswordPage, TopNav, etc.) keeps working unchanged; only the
// implementation underneath moved from Supabase to Convex.
//
// Known gap from this pass: only frontend/src/hooks/useAuth.tsx + the
// login/register/cases pages were wired to Convex. Everything else in
// frontend/src/ (services/api.ts, lib/supabase.ts and its ~130 other
// consumers) still expects a Supabase session to read the bearer token from.
// A user who signs in via this new Convex-Auth flow will NOT have a Supabase
// session, so any still-unmigrated page that calls the Express API will fail
// to authenticate until it's ported in a future round. See
// CONVEX_MIGRATION_STATUS.md "What's NOT done" for the tracked list.
import React, { createContext, useContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useConvexAuth } from 'convex/react';
import { useAuthActions } from '@convex-dev/auth/react';
import { api } from '../../convex/_generated/api';
import type { Profile, Firm } from '../types';
import { demoProfile, demoFirm } from '../data/demoData';

function isDemoMode(): boolean {
  if (import.meta.env.VITE_DEMO_MODE === 'true') return true;
  try {
    return sessionStorage.getItem('demo_mode') === 'true' || localStorage.getItem('demo_mode') === 'true';
  } catch {
    return false;
  }
}

function setDemoMode(enabled: boolean) {
  try {
    if (enabled) {
      sessionStorage.setItem('demo_mode', 'true');
      localStorage.setItem('demo_mode', 'true');
    } else {
      sessionStorage.removeItem('demo_mode');
      localStorage.removeItem('demo_mode');
    }
  } catch {
    // ignore
  }
}

// Convex `users` role strings ("Attorney" | "Company Admin" | "Super Admin")
// map onto the old Profile.role space ("owner" | "admin" | "attorney" |
// "paralegal") the rest of the app (ProtectedRoute's requiredRole, etc.)
// still expects. Not a 1:1 mapping — paralegal has no Convex-side equivalent
// yet — but this keeps role-gated routes working with real data instead of
// silently granting/denying access.
function toLegacyRole(role: string | undefined): Profile['role'] {
  switch (role) {
    case 'Super Admin':
      return 'owner';
    case 'Company Admin':
      return 'admin';
    default:
      return 'attorney';
  }
}

interface AuthContextType {
  user: Profile | null;
  firm: Firm | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isDemoMode: boolean;
  login: (email: string, password: string) => Promise<{ error: Error | null }>;
  signup: (
    email: string,
    password: string,
    fullName: string,
    barNumber: string,
    firmName?: string
  ) => Promise<{ error: Error | null }>;
  logout: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: Error | null }>;
  confirmPasswordReset: (
    email: string,
    code: string,
    newPassword: string
  ) => Promise<{ error: Error | null }>;
  updateProfile: (updates: Partial<Profile>) => Promise<{ error: Error | null }>;
  demoLogin: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const { signIn, signOut } = useAuthActions();
  const { isAuthenticated: convexAuthed, isLoading: convexLoading } = useConvexAuth();
  const [demoModeActive, setDemoModeActive] = useState(() => isDemoMode());
  const completeSignup = useMutation(api.users.completeSignup);
  const updateProfileMutation = useMutation(api.users.updateProfile);

  // `"skip"` when signed out, so this doesn't fire an unauthenticated query
  // every render — currentUser already returns null unauthenticated, but
  // skipping avoids the extra round trip while the demo-mode path is active.
  const currentUser = useQuery(api.users.currentUser, convexAuthed ? {} : 'skip');

  // Onboarding (completeSignup) is driven by this effect instead of being
  // called directly inside signup() right after signIn() resolves — found
  // live during this migration pass: signIn()'s promise resolving does NOT
  // guarantee the underlying ConvexReactClient has finished propagating the
  // new auth token yet (that happens via ConvexAuthProvider's own effect, a
  // render tick later), so a mutation fired immediately after signIn() can
  // race ahead and hit the server unauthenticated. Waiting for
  // useConvexAuth().isAuthenticated + a successfully loaded currentUser
  // guarantees the token really is attached. Idempotent (completeSignup
  // no-ops if companyId is already set), so this also self-heals any
  // account that somehow ended up signed in without a company.
  const pendingSignupExtras = useRef<{ firstName?: string; lastName?: string; firmName?: string } | null>(null);
  useEffect(() => {
    if (!convexAuthed || !currentUser || currentUser.companyId) return;
    const extras = pendingSignupExtras.current ?? {};
    pendingSignupExtras.current = null;
    completeSignup(extras).catch((err) => {
      console.error('completeSignup failed:', err);
    });
  }, [convexAuthed, currentUser, completeSignup]);

  // Real Convex Auth always wins over a stale demo_mode flag. Found live:
  // once demo_mode was set true (e.g. from a prior "Try Demo" click), NOTHING
  // ever cleared it on a successful real sign-in/registration — `user` above
  // checks `demoModeActive` first regardless of `currentUser`, so a genuinely
  // authenticated account still rendered as the canned demo profile, and
  // every isDemoMode() check elsewhere in the app (Cases/Witnesses/etc. pages)
  // kept routing to the old Express demo client instead of Convex. Combined
  // with login()'s stale isDemoMode() re-check below, a browser that had ever
  // entered demo mode was effectively stuck there through sign-out and
  // registration alike.
  useEffect(() => {
    if (convexAuthed && currentUser && isDemoMode()) {
      setDemoMode(false);
      setDemoModeActive(false);
    }
  }, [convexAuthed, currentUser]);

  const user: Profile | null = useMemo(() => {
    if (demoModeActive) return demoProfile as Profile;
    if (!currentUser) return null;
    const fullName = [currentUser.firstName, currentUser.lastName].filter(Boolean).join(' ') || currentUser.email;
    return {
      id: currentUser._id,
      email: currentUser.email ?? '',
      full_name: fullName || null,
      avatar_url: null,
      bar_number: null,
      phone: null,
      role: toLegacyRole(currentUser.role),
      firm_id: currentUser.companyId ?? null,
      created_at: new Date(currentUser._creationTime).toISOString(),
      updated_at: new Date(currentUser._creationTime).toISOString(),
    } as Profile;
  }, [demoModeActive, currentUser]);

  const firm: Firm | null = useMemo(() => {
    if (demoModeActive) return demoFirm as Firm;
    const company = (currentUser as any)?.company;
    if (!company) return null;
    return {
      id: company._id,
      name: company.name,
      slug: company.slug ?? '',
      logo_url: null,
      primary_color: company.primaryColor ?? '#2563eb',
      subscription_tier: (company.subscriptionTier ?? 'free') as Firm['subscription_tier'],
    } as unknown as Firm;
  }, [demoModeActive, currentUser]);

  // Loading until we know either (a) demo mode is active, or (b) Convex Auth
  // has resolved AND, if authenticated, currentUser has returned at least
  // once (undefined = still loading, null = loaded-and-signed-out).
  const isLoading =
    !demoModeActive && (convexLoading || (convexAuthed && currentUser === undefined));

  const login = useCallback(
    async (email: string, password: string): Promise<{ error: Error | null }> => {
      // Only the email actually being submitted decides demo mode here — a
      // stale isDemoMode() flag from an earlier session must not force every
      // subsequent login attempt back into demo mode regardless of the real
      // credentials entered (see the reconciling effect above for the other
      // half of this fix: real auth succeeding also clears the stale flag).
      if (email.toLowerCase().includes('demo')) {
        setDemoMode(true);
        setDemoModeActive(true);
        return { error: null };
      }
      try {
        await signIn('password', { email, password, flow: 'signIn' });
        return { error: null };
      } catch {
        // Convex Auth's Password provider throws bare `Error`s (not
        // `ConvexError`), which Convex redacts into an opaque
        // "[CONVEX A(auth:signIn)] ... Server Error" string on the client —
        // that string must never reach the login form; it's meaningless to
        // a user and doesn't distinguish "no account" from "wrong password"
        // anyway, so a single friendly message is the honest thing to show.
        return { error: new Error('Invalid email or password') };
      }
    },
    [signIn]
  );

  const signup = useCallback(
    async (
      email: string,
      password: string,
      fullName: string,
      _barNumber: string,
      firmName?: string
    ): Promise<{ error: Error | null }> => {
      const [firstName, ...rest] = fullName.trim().split(/\s+/);
      // Stash for the onboarding effect above to pick up once currentUser
      // has actually loaded under the new auth token — see that effect's
      // comment for why completeSignup isn't just called right here.
      pendingSignupExtras.current = { firstName, lastName: rest.join(' '), firmName };
      try {
        await signIn('password', { email, password, flow: 'signUp' });
        return { error: null };
      } catch (err: any) {
        pendingSignupExtras.current = null;
        // Same redaction issue as login() above — but signUp's one common,
        // genuinely distinguishable failure (duplicate email) DOES surface
        // recognizably in the raw message, so keep that one case specific.
        const message = /account already exists|already.*exist/i.test(err?.message ?? '')
          ? 'An account with this email already exists.'
          : 'Sign up failed. Please check your details and try again.';
        return { error: new Error(message) };
      }
    },
    [signIn]
  );

  const logout = useCallback(async () => {
    setDemoMode(false);
    setDemoModeActive(false);
    if (convexAuthed) {
      await signOut();
    }
  }, [signOut, convexAuthed]);

  const resetPassword = useCallback(
    async (email: string): Promise<{ error: Error | null }> => {
      try {
        await signIn('password', { email, flow: 'reset' });
        return { error: null };
      } catch {
        // Same "don't leak the raw Convex error" reasoning as login() above.
        // Deliberately does not reveal whether the account exists — the UI
        // always shows the same "check your email" message either way.
        return { error: null };
      }
    },
    [signIn]
  );

  const confirmPasswordReset = useCallback(
    async (email: string, code: string, newPassword: string): Promise<{ error: Error | null }> => {
      try {
        await signIn('password', { email, code, newPassword, flow: 'reset-verification' });
        return { error: null };
      } catch {
        return { error: new Error('That code is invalid or has expired. Request a new one.') };
      }
    },
    [signIn]
  );

  const updateProfile = useCallback(
    async (updates: Partial<Profile>): Promise<{ error: Error | null }> => {
      if (!user) return { error: new Error('Not authenticated') };
      try {
        const [firstName, ...rest] = (updates.full_name ?? '').trim().split(/\s+/);
        await updateProfileMutation({
          firstName: updates.full_name ? firstName : undefined,
          lastName: updates.full_name ? rest.join(' ') : undefined,
        });
        return { error: null };
      } catch (err: any) {
        return { error: new Error(err?.message || 'Failed to update profile') };
      }
    },
    [user, updateProfileMutation]
  );

  const demoLogin = useCallback(async () => {
    setDemoMode(true);
    setDemoModeActive(true);
  }, []);

  const isAuthenticated = demoModeActive || convexAuthed;

  return (
    <AuthContext.Provider
      value={{
        user,
        firm,
        isLoading,
        isAuthenticated,
        isDemoMode: demoModeActive,
        login,
        signup,
        logout,
        resetPassword,
        confirmPasswordReset,
        updateProfile,
        demoLogin,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default useAuth;
